import { memo, useState, useEffect, useRef } from 'react'
import { scaleQuantize } from '@visx/scale'
import { scaleSymlog, scaleDiverging, scaleDivergingSymlog } from 'd3-scale' // Not included in vx/scales
import { interpolateBrBG, interpolateOrRd, interpolateGnBu } from 'd3-scale-chromatic'
import { Zoom } from '@visx/zoom'

import { SCALE_TYPES } from './constants'
import { MapKey, areMapKeysEqual } from './map-key'
import { MapLayer, areMapLayersEqual } from './map-layer'
import { PrimaryMap, arePrimaryMapsEqual } from './primary-map'
import { getProjectionComponent } from './projection'
import { SelectedArea, areSelectedAreasEqual } from './selected-area'

import { useTooltipDisplayContext } from '../common/tooltip/tooltip.context'
import { Tooltip } from '../common/tooltip/tooltip'
import { formatService, FORMAT_TYPES } from '../common/format.service'

import { useStyles } from './base-map.style'

const colorRanges = {
  red: ['#FBE0D9', '#F9D1C7', '#f6b3a2', '#f2947d', '#ef7658', '#ec5833', '#C2492A', '#973921', '#6c2818', '#41180e'],
  blue: ['#DAE7EA', '#C8DBE0', '#A3C4CB', '#7FACB7', '#5A95A2', '#367E8E', '#2D6875', '#23515B', '#193A41', '#0F2327']
}

const SYMLOG_CONST_DEFAULT = 1

const PrimaryMapMemo = memo(PrimaryMap, arePrimaryMapsEqual)
const SelectedAreaMemo = memo(SelectedArea, areSelectedAreasEqual)
const MapLayerMemo = memo(MapLayer, areMapLayersEqual)
const MapKeyMemo = memo(MapKey, areMapKeysEqual)

/**
 * Base Map Component
 *
 * @param {number} width Width as a number of pixels
 * @param {number} height Height as a number of pixels
 * @param {number} projectionType Type of projection to use
 * @param {object} projectionProps Props for the projection component (ie, scale & translate)
 * @param {object} toplogy Topology as GeoJSON features
 * @param {function} mapRegionInfo Function for extracting region info from the GeoJSON feature properties
 * @param {object} regionData Key: unique identifier for the region, Value: number used for coloring the region
 * @param {string} regionLabel Display label that what type of region is displayed (ie, "county" or "state")
 * @param {string} valueLabel Display label that identifies what the `values` numbers represent (ie, "number of deaths")
 * @param {string} valueType What type of value is being represented ('percent' or 'number') - default: number
 * @param {object} layer.topology Topolgy that will be layered on top of the primary topology
 * @param {string} layer.label Display label that displays on the tooltip
 * @param {object} layer.data Data that gets displayed on the tooltip. The object keys map to regionInfo codes.
 * @param {string} mapKeyNote Display note under the map key (ie, "as of July 2019")
 * @param {object} mapKeyPosition Pixels to shift the map key (`bottom` and `left` properties)
 * @param {string} scaleOptions.type Type of scale ('quantize' or 'diverging') - default: quantize
 * @param {number} scaleOptions.divergingFrom When using diverging scale - the number to diverge from - default: 0
 * @param {number} scaleOptions.constant When using symlog or diverging symlog scale, constant can be specified to
 *  control how quickly/slowly the logarithmic curve reaches the darkest color. Use 1, 10, 100, 1000, 10000, etc. Default is 1.
 *  more info: https://github.com/d3/d3-scale#symlog_constant
 * @param {string} colorRange Color set to use ('red' or 'blue')
 * @param {object} valueRange Object with `min` and `max` properties allowing the hardcoding of the map scale
 * @oaram {boolean} enableZoom Enable/disable zooming
 * @param {array} referenceData Reference data that provides cross-references
 * @param {function} onClick Called when a user clicks on a map area
 *
 * The rest of the props are from the vx Tooltip Higher Order Component
 */
export const BaseMap = function ({
  width,
  height,
  projectionType,
  projectionProps,
  topology,
  mapRegionInfo,
  regionData,
  regionLabel,
  valueLabel,
  valueType = FORMAT_TYPES.NUMBER,
  value2Type = FORMAT_TYPES.NUMBER,
  layer = {},
  mapKeyNote,
  mapKeyPosition,
  scaleOptions = { type: SCALE_TYPES.SYMLOG, constant: SYMLOG_CONST_DEFAULT },
  colorRange = 'red',
  valueRange = {},
  enableZoom = false,
  referenceData,
  onClick,
  showKey = true,
  keyOffset = [0, 0],
  zoomState,
  onZoomIn,
  onZoomOut
}) {
  const classes = useStyles()

  const [selectedFeature, setSelectedFeature] = useState()
  const [{ scale }, setScale] = useState({})
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const zoomRef = useRef()
  const currentZoomStateRef = useRef()
  const [, setTooltipDisplayData] = useTooltipDisplayContext()
  let { min, max } = valueRange

  const Projection = getProjectionComponent(projectionType)

  const initialZoomTransform = {
    scaleX: 1,
    scaleY: 1,
    translateX: 0,
    translateY: 0,
    skewX: 0,
    skewY: 0
  }

  useEffect(() => {
    if (!regionData) {
      return
    }

    let values = []

    if (min === undefined || max === undefined) {
      values = topology.features
        .map((feature) => mapRegionInfo(feature).code)
        .reduce((values, code) => regionData[code] ? values.concat(regionData[code].value) : values, values)
    }

    let minValue = (min === undefined) ? Math.min(...values) : min
    let maxValue = (max === undefined) ? Math.max(...values) : max

    // Make max to be 1 so that 0 will be colored with a light color.
    if (minValue === 0 && maxValue === 0) {
      maxValue = 1
    }

    let newScale
    if (scaleOptions.type === SCALE_TYPES.DIVERGING) {
      newScale = scaleDiverging(interpolateBrBG)
        .domain([
          minValue,
          (scaleOptions.divergingFrom === undefined) ? 0 : scaleOptions.divergingFrom,
          maxValue
        ])
    } else if (scaleOptions.type === SCALE_TYPES.DIVERGING_SYMLOG) {
      newScale = scaleDivergingSymlog(interpolateBrBG)
        .domain([
          minValue,
          (scaleOptions.divergingFrom === undefined) ? 0 : scaleOptions.divergingFrom,
          maxValue
        ])
        .constant(scaleOptions.constant || SYMLOG_CONST_DEFAULT)
    } else if (scaleOptions.type === SCALE_TYPES.QUANTIZE) {
      newScale = scaleQuantize({
        domain: [ minValue, maxValue ],
        range: colorRanges[colorRange]
      })
    } else {
      let chromatic = interpolateOrRd
      if (colorRange === 'blue') {
        chromatic = interpolateGnBu
      }

      newScale = scaleSymlog()
        .domain([minValue, maxValue])
        .interpolate(() => (chromatic))
        .constant(scaleOptions.constant || SYMLOG_CONST_DEFAULT)
    }
    setScale({ scale: newScale })
  }, [ regionData, min, max, colorRange, scaleOptions.type, scaleOptions.constant, scaleOptions.divergingFrom ])

  useEffect(() => {
    let zoomRefHolder = zoomRef.current
    if (!enableZoom || !zoomRefHolder) {
      return
    }

    if (isZoomedIn) {
      zoomRefHolder.scale({ scaleX: 4, scaleY: 4 })
    } else {
      zoomRef.current = undefined
      currentZoomStateRef.current = undefined
    }
  }, [isZoomedIn])

  function getRefDataForRegion (region) {
    if (!referenceData) {
      return
    }

    return Object.keys(referenceData).reduce((acc, r) => {
      let value = referenceData[r][region]
      if (value !== undefined) {
        value = formatService.formatPercentShorter(value)
        acc.push({ label: r, value })
      }
      return acc
    }, [])
  }

  function showSelected (feature, regionInfo, valueType, value2Type) {
    setSelectedFeature({ feature, data: regionInfo })
    setTooltipDisplayData({
      primaryValue: regionInfo.value === undefined ? 'No Data' : formatService.formatValue(regionInfo.value, valueType),
      secondaryValue: layer.data ? [ regionInfo.name, layer.data[regionInfo.code] ] : [ regionInfo.name ],
      tertiaryValue: (regionInfo.value2 !== undefined) ? formatService.formatValue(regionInfo.value2, value2Type) : undefined,
      referenceData: getRefDataForRegion(regionInfo.code)
    })
  }

  function hideSelected () {
    setSelectedFeature()
    setTooltipDisplayData()
  }

  useEffect(() => {
    if (currentZoomStateRef.current && !zoomState) {
      zoomRef.current.clear()
      setIsZoomedIn(false)
    } else if (!currentZoomStateRef.current && zoomState) {
      zoomRef.current.setTranslate(zoomState)
      setIsZoomedIn(true)
    }
    currentZoomStateRef.current = zoomState
  }, [zoomState])

  function handleZoomClick (event, zoom) {
    zoomRef.current = zoom
    const elemBox = event.currentTarget.getBBox()
    const zoomData = {
      translateX: ((width / 2) - elemBox.x - (elemBox.width / 2)),
      translateY: ((height / 2) - elemBox.y - (elemBox.height / 2))
    }
    if (onZoomIn) {
      // Let parent handle zooming
      onZoomIn(zoomData)
    } else {
      // Handle zoom changes within this component

      // Set the zoom ref up above and then
      zoom.setTranslate(zoomData)
      setIsZoomedIn(true)
    }
  }

  function handleZoomOut (zoom) {
    if (onZoomOut) {
      zoomRef.current = zoom
      onZoomOut()
    } else {
      zoomRef.current = zoom
      zoom.clear()
      setIsZoomedIn(false)
    }
  }

  if (!regionData || !scale) {
    // This prevents map from causing shifts in content when the map is loaded the first time.
    return <div style={{ width, height }} />
  }

  const keyPosition = {
    position: 'absolute',
    ...mapKeyPosition
  }

  if (keyPosition.bottom !== undefined && keyOffset[0]) {
    keyPosition.bottom += keyOffset[0]
  }

  if (keyPosition.left !== undefined && keyOffset[1]) {
    keyPosition.left += keyOffset[1]
  }

  return (
    <>
      <div style={{ position: 'relative' }} data-qa="map-chart">
        <Zoom
          width={width}
          height={height}
          scaleXMin={1}
          scaleXMax={4}
          scaleYMin={1}
          scaleYMax={4}
          transformMatrix={initialZoomTransform}
          passive={true}
          onWheel={function () {}} // Prevent scroll wheel from zooming map
        >
          {(zoom) => {
            zoomRef.current = zoom

            // Change view box if the map is ncmap to eliminate gap
            const viewBoxSize = projectionType === 'ncmap' ? '0 0 1000 500' : '0 0 1000 600'
            return (
              <>
                <svg viewBox={viewBoxSize} width={'100%'} className={classes.svg}
                  onMouseOver={(event) => {
                    if (!event.target.ownerSVGElement) {
                      hideSelected()
                    }
                  }}
                  onMouseLeave={() => {
                    // There is a case where moving the mouse quickly out of the map causes it to
                    // not hide the tooltip as it should. Need to manually hide the tooltip here.
                    hideSelected()
                  }}
                >
                  <g transform={zoom.toString()}>
                    <Projection data={topology.features} {...projectionProps}>
                      {(projection) => {
                        const isLayerEnabled = layer.topology
                        return (
                          <g>
                            <PrimaryMapMemo projection={projection} mapRegionInfo={mapRegionInfo} regionData={regionData} scale={scale} isLayerEnabled={isLayerEnabled}
                              valueLabel={valueLabel} enableZoom={enableZoom} isZoomedIn={isZoomedIn} showSelected={showSelected} valueType={valueType} value2Type={value2Type} width={width} height={height} />
                            {selectedFeature &&
                              <SelectedAreaMemo projection={projection} selectedFeature={selectedFeature} scale={scale} isLayerEnabled={isLayerEnabled}
                                isZoomedIn={isZoomedIn} onClick={onClick} width={width} height={height} />
                            }
                          </g>
                        )
                      }}
                    </Projection>
                    <MapLayerMemo topology={layer.topology} projectionType={projectionType} projectionProps={projectionProps} zoom={zoom}
                      handleZoomClick={handleZoomClick} isZoomedIn={isZoomedIn} width={width} height={height} />
                  </g>
                </svg>
                {isZoomedIn && (
                  <div className={classes.zoomOut}>
                    <button className={classes.zoomOutButton} onClick={() => { handleZoomOut(zoom) }}>Zoom Out</button>
                  </div>
                )}
              </>
            )
          }}
        </Zoom>
        <div style={keyPosition}>
          {showKey && scale &&
            <MapKeyMemo
              valueLabel={valueLabel}
              scale={scale}
              mapKeyNote={mapKeyNote}
              valueType={valueType} />
          }
        </div>
      </div>
      <Tooltip defaults={{ primaryLabel: valueLabel, secondaryLabel: layer.label ? [ regionLabel, layer.label ] : [ regionLabel ] }} />
    </>
  )
}
